// @flow
import 'isomorphic-fetch'
import { API, createDefaultHeaders } from '../../util/api'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import { logger } from '../../util/logger'
import * as actionCreators from './index'
import * as deviceActionCreators from '../device/index'
import * as scheduleSchema from './schemas'
import changeCase from 'change-object-case'

import type { Dispatch } from '../../util/redux'

/**
 * A module that exports `create`, `get`, and `update` functions for schedules
 * @module
 */

const Api = new API()

/**
 * Create schedule
 * @param {string} userAuthToken - User's authorization token
 * @param {object} scheduleData - Schedule data
 * @returns {object} An action related to the schedule create process
 */
export function create(userAuthToken: string, scheduleData: Object) {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(scheduleData, {
      recursive: true,
      arrayRecursive: true
    })
    const error = isInvalid(scheduleSchema.CreateScheduleRequestSchema, reqData)
    const reqObj = {
      method: 'POST',
      body: JSON.stringify(reqData),
      headers: new Headers({
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      })
    }

    dispatch(actionCreators.scheduleCreateInit(reqData))

    if (error) {
      logger.error(error)
      return Promise.reject(
        dispatch(actionCreators.scheduleCreateComplete(error))
      )
    }

    return Api.call('schedule/createSchedule', reqObj)
      .then(response => {
        if (response.status === 409) {
          throw new Error('Schedule already exist in system')
        }
        return response
      })
      .then(response => response.json())
      .then(json => {
        const deviceId = json.schedule.zoneInfo[0].deviceId

        dispatch(
          deviceActionCreators.addScheduleIds({
            deviceId,
            scheduleIds: [json.schedule.id]
          })
        )
        return dispatch(actionCreators.scheduleCreateComplete(json))
      })
      .catch(err => {
        logger.error(err)
        return dispatch(actionCreators.scheduleCreateComplete(err))
      })
  }
}

/**
 * Get schedule
 * @param {string} userAuthToken - User's authorization token
 * @param {string} scheduleId - Schedule id
 * @returns {object} An action related to the schedule retrieval process
 */
export function get(userAuthToken: string, scheduleId: string) {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(
      { scheduleId: { id: [scheduleId] } },
      { recursive: true }
    )
    const error = isInvalid(scheduleSchema.GetSchedulesRequestSchema, reqData)
    const reqObj = {
      method: 'POST',
      body: JSON.stringify(reqData),
      headers: new Headers({
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      })
    }
    const missingScheduleError = new Error(
      `Schedule ID ${scheduleId} not found on server`
    )
    // $FlowFixMe
    missingScheduleError.scheduleId = scheduleId

    dispatch(actionCreators.scheduleGetInit({ scheduleId }))

    if (error) {
      logger.error(error)
      return Promise.reject(dispatch(actionCreators.scheduleGetComplete(error)))
    }

    return Api.call('schedule/getSchedules', reqObj)
      .then(response =>
        response.json().then(json => {
          if (json.schedule[0]) {
            dispatch(
              actionCreators.scheduleGetComplete({ schedule: json.schedule[0] })
            )
          } else {
            dispatch(actionCreators.scheduleGetComplete(missingScheduleError))
          }
        })
      )
      .catch(err => {
        logger.error(err)
        return dispatch(actionCreators.scheduleGetComplete(err))
      })
  }
}

/**
 * Update schedule
 * @param {string} userAuthToken - User's authorization token
 * @param {string} updateData - Update data for request
 * @returns {object} An action related to the schedule update process
 */
export function update(userAuthToken: string, updateData: Object) {
  return function(dispatch: Dispatch) {
    const reqData = changeCase.snakeKeys(updateData, {
      recursive: true,
      arrayRecursive: true
    })
    const error = isInvalid(scheduleSchema.UpdateScheduleRequestSchema, reqData)
    const reqObj = {
      method: 'PUT',
      body: JSON.stringify(reqData),
      headers: new Headers({
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      })
    }

    dispatch(actionCreators.scheduleUpdateInit(updateData))

    if (error) {
      logger.error(error)
      return Promise.reject(
        dispatch(actionCreators.scheduleUpdateComplete(error))
      ).catch(err => err)
    }

    return Api.call('schedule/updateSchedule', reqObj)
      .then(response =>
        response
          .json()
          .then(json => dispatch(actionCreators.scheduleUpdateComplete(json)))
      )
      .catch(err => {
        err.scheduleId = updateData.scheduleId
        logger.error(err)
        return dispatch(actionCreators.scheduleUpdateComplete(err))
      })
  }
}

/**
 * Delete schedule
 * @param {string} authToken - User's authorization token
 * @param {string} scheduleId - Schedule to delete's id
 * @returns {object} An action related to the schedule deletion process
 */
export function del(authToken: string, scheduleId: string) {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(
      { scheduleId: scheduleId },
      { recursive: true }
    )
    const error = isInvalid(
      scheduleSchema.DeleteSchedulesRequestSchema,
      reqData
    )
    const headers = {
      ...createDefaultHeaders(authToken),
      'Content-Type': 'application/json'
    }
    const reqObj = {
      method: 'DELETE',
      headers: new Headers(headers)
    }

    if (error) {
      logger.error(error)
      return Promise.reject(
        dispatch(actionCreators.scheduleDeleteComplete(error))
      )
    }

    dispatch(actionCreators.scheduleDeleteInit({ scheduleId }))

    return Api.call('schedule/deleteSchedule/' + scheduleId, reqObj)
      .then(response => response.json())
      .then(res => {
        if (res.deleted) {
          return dispatch(actionCreators.scheduleDeleteComplete({ scheduleId }))
        } else {
          return Promise.reject(res)
        }
      })
      .catch(err => {
        logger.error(err)
        return dispatch(actionCreators.scheduleDeleteComplete(err))
      })
  }
}
